export interface ISubscriptionListener<DataType = unknown, ContextType = unknown> {
  (data: DataType, context?: ContextType): void
}
interface ISubscriptionEntry<DataType = unknown, ContextType = unknown> {
  listener: ISubscriptionListener<DataType, ContextType>
  context: ContextType
}
export interface ISubscriptionUnsubscribe {
  (): void
}

export interface ISubscriptionCatchReporter<ContextType = unknown> {
  (error: unknown, context?: ContextType): void
}

export interface ISubscription<DataType = unknown, ContextType = unknown> {
  listen(listener: ISubscriptionListener<DataType, ContextType>, context?: ContextType): ISubscriptionUnsubscribe
  update(data: DataType): void
  clear(): void
  suspend(): void
  get suspended(): boolean
  resume(): void
  catch(reporter: ISubscriptionCatchReporter<ContextType>): void
}

export default function Subscription<DataType = unknown, ContextType = unknown>(): ISubscription<
  DataType,
  ContextType
> {
  const subscriptions: Set<ISubscriptionEntry<DataType, ContextType>> = new Set()
  let suspended = false
  let onCatch: ISubscriptionCatchReporter<ContextType> = null
  return {
    listen(listener: ISubscriptionListener<DataType, ContextType>, context: ContextType) {
      if (listener) {
        const subscriptionEntry: ISubscriptionEntry<DataType, ContextType> = { listener, context }
        subscriptions.add(subscriptionEntry)
        return () => void subscriptions.delete(subscriptionEntry)
      } else {
        return () => void 0
      }
    },
    update(data: DataType) {
      if (!suspended) {
        for (const subscriptionEntry of subscriptions) {
          try {
            subscriptionEntry.listener(data, subscriptionEntry.context)
          } catch (error) {
            if (onCatch) {
              onCatch(error, subscriptionEntry.context)
            }
          }
        }
      }
    },
    clear() {
      subscriptions.clear()
    },
    suspend() {
      suspended = true
    },
    get suspended() {
      return suspended
    },
    resume() {
      suspended = false
    },
    catch(reporter) {
      onCatch = reporter
    }
  }
}
